import { useState, useEffect, useCallback, useRef } from 'react';
import Persona from 'persona';
import { supabase } from './supabaseClient';
import NavDrawer from './NavDrawer';
import TrustBadge from './components/TrustBadge';
import { exportCollectionCsv, exportCollectionXlsx } from './lib/collectionExportRun';
import CollectionImport from './components/CollectionImport';
import DeleteAccountSection from './DeleteAccountSection';
import { getTheme, setTheme } from './theme';

// Persona template + environment ID — public identifiers, embedded at build time.
// Set in Netlify env vars (staging + prod). If absent, the Get-verified CTA
// renders disabled with a helper note rather than crashing.
const PERSONA_TEMPLATE_ID = process.env.REACT_APP_PERSONA_TEMPLATE_ID;
const PERSONA_ENVIRONMENT_ID = process.env.REACT_APP_PERSONA_ENVIRONMENT_ID;

// Webhook race window: Persona's onComplete fires before the webhook reaches our
// edge function. Poll the profile row until identity_verified flips, or give up
// after 15s and tell the user to refresh.
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 15000;
const HARD_FAIL_THRESHOLD = 3; // mirrors the edge function

const BIO_MAX = 250;
const AVATAR_SIZE = 256;

// Change-account-email flow: RESEND cooldown mirrors the signup panel in
// AuthPage.js. The localStorage key remembers which address a change moved
// away from, so the "update your Marketplace contact email too?" prompt can
// fire even when the confirmation links are clicked in another tab or visit.
const EMAIL_RESEND_COOLDOWN_S = 60;
const EMAIL_CHANGE_FROM_KEY = 'ck-email-change-from';

// Sensible defaults at top of list, then alphabetical worldwide-ish set.
// Wider list can ship in a follow-up if a non-US collector requests their country.
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
];

// ─── Avatar resize utility ────────────────────────────────────────────
// Center-crops to a square and resamples to AVATAR_SIZE × AVATAR_SIZE JPEG.
// Keeps avatar files ~10–25KB; lazy-loaded everywhere they render.
function resizeAvatarSquare(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = AVATAR_SIZE;
      canvas.height = AVATAR_SIZE;
      const ctx = canvas.getContext('2d');
      const minDim = Math.min(img.width, img.height);
      const sx = (img.width - minDim) / 2;
      const sy = (img.height - minDim) / 2;
      ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (blob) resolve(blob); else reject(new Error('canvas.toBlob returned null'));
        },
        'image/jpeg',
        0.88,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')); };
    img.src = url;
  });
}

export default function ProfilePage({ user, onBack }) {
  const [username, setUsername] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  // ─── Change account email (Supabase secure email change) ──────────
  // authUser is the freshest auth record — the `user` prop can come from a
  // cached session on load and miss a pending change; new_email present
  // means a change is awaiting its two confirmation clicks.
  const [authUser, setAuthUser] = useState(user);
  const [emailPanelOpen, setEmailPanelOpen] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [emailChangeBusy, setEmailChangeBusy] = useState(false);
  const [emailChangeMsg, setEmailChangeMsg] = useState('');
  const [emailSentTo, setEmailSentTo] = useState(''); // shown if new_email is ever absent after a successful request
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);
  const [contactEmailPrompt, setContactEmailPrompt] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  // Dark Side / Light Side. Applies instantly (body class + localStorage) so
  // the user previews it live; not part of the page's SAVE payload.
  const [theme, setThemeState] = useState(getTheme());
  const chooseTheme = (t) => { setTheme(t); setThemeState(t); };
  const [memberSince, setMemberSince] = useState('');
  const [stats, setStats] = useState({ collected: 0, wishlist: 0, graded: 0, sets: 0 });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  // Collection export strip (COLLECTION STATS card): which format is mid-export
  // / just finished ('xlsx' | 'csv' | null). Both buttons disable while either
  // export runs; ✓ DOWNLOADED shows on the clicked one for 3s.
  const [exporting, setExporting] = useState(null);
  const [exportDone, setExportDone] = useState(null);
  const [exportError, setExportError] = useState('');
  // Phase 2E.2 bug-fix follow-up: when a profile save leaves the seller with
  // no valid payment methods (toggle off OR underlying handle/email empty),
  // we auto-pause their for-sale listings. This notice surfaces how many we
  // paused so the action is visible, per BUILD_GUIDE § 6.4 edge case.
  const [pausedNotice, setPausedNotice] = useState('');

  // Verification state (Phase 2E.1)
  const [identityVerified, setIdentityVerified] = useState(false);
  const [verifiedAt, setVerifiedAt] = useState(null);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [verificationLockedUntil, setVerificationLockedUntil] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyToast, setVerifyToast] = useState('');
  const personaClientRef = useRef(null);

  // Profile-extension state (Phase 2E.1 Step 2a)
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarCacheBust, setAvatarCacheBust] = useState(0); // forces <img> refresh after re-upload
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarFileInputRef = useRef(null);

  const [legalFirstName, setLegalFirstName] = useState('');
  const [legalLastName, setLegalLastName] = useState('');

  const [addrLine1, setAddrLine1] = useState('');
  const [addrLine2, setAddrLine2] = useState('');
  const [addrCity, setAddrCity] = useState('');
  const [addrState, setAddrState] = useState('');
  const [addrPostal, setAddrPostal] = useState('');
  const [addrCountry, setAddrCountry] = useState('US');
  const [addrPhone, setAddrPhone] = useState('');

  const [acceptsPaypal, setAcceptsPaypal] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [acceptsVenmo, setAcceptsVenmo] = useState(false);
  const [venmoUsername, setVenmoUsername] = useState('');

  // Phase 2E.1 addition: MARKETPLACE TRUST card
  // sellerStats / buyerStats from the user_trust_stats view (Phase 2D);
  // history rows from the marketplace-trust-history edge function (Step 4).
  const [sellerStats, setSellerStats] = useState(null); // { count, color, clean, unresolved, flagged } | null
  const [buyerStats, setBuyerStats] = useState(null);
  const [historyData, setHistoryData] = useState(null); // { seller: { count, rows }, buyer: { count, rows } } | null
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const [trustTab, setTrustTab] = useState('seller');

  // Global "show my @username in transaction records" toggle. Writes
  // profiles.share_transaction_history_publicly (read server-side by
  // marketplace-trust-history). Controls ONLY whether THIS user's handle is
  // exposed when they're a counterparty in someone else's history — it does
  // not change what this user sees in their own rows. Per-transaction
  // overrides are roadmapped (2.1, Gap #9). Immediate-write, not tied to Save.
  const [shareHistory, setShareHistory] = useState(false);
  const [shareHistorySaving, setShareHistorySaving] = useState(false);
  const toggleShareHistory = useCallback(async () => {
    if (shareHistorySaving) return;
    const next = !shareHistory;
    setShareHistory(next); // optimistic
    setShareHistorySaving(true);
    const { error: err } = await supabase
      .from('profiles')
      .update({ share_transaction_history_publicly: next })
      .eq('id', user.id);
    setShareHistorySaving(false);
    if (err) setShareHistory(!next); // revert on failure
  }, [shareHistory, shareHistorySaving, user.id]);

  // ─── Change account email: effects + handlers ─────────────────────
  useEffect(() => {
    if (emailResendCooldown <= 0) return;
    const t = setTimeout(() => setEmailResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [emailResendCooldown]);

  // Fresh auth record on load (picks up a pending change), re-fetched when the
  // change completes and App's USER_UPDATED listener flips user.email.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => {
      if (!cancelled && data?.user) setAuthUser(data.user);
    });
    return () => { cancelled = true; };
  }, [user.id, user.email]);

  // After a change completes (this visit or a past one), offer to move the
  // Marketplace contact email off the old address if it still points there.
  useEffect(() => {
    const from = localStorage.getItem(EMAIL_CHANGE_FROM_KEY);
    const current = authUser?.email || user.email;
    if (!from || !contactEmail || !current) return;
    if (current.toLowerCase() === from.toLowerCase()) return; // change still pending
    if (contactEmail.toLowerCase() === from.toLowerCase()) setContactEmailPrompt(true);
    else localStorage.removeItem(EMAIL_CHANGE_FROM_KEY); // contact email already elsewhere
  }, [contactEmail, user.email, authUser]);

  // Prefer the fresh auth record for display — a cached session can carry the
  // old address until its JWT refreshes (e.g. the change completed on another
  // device or in another tab).
  const accountEmail = authUser?.email || user.email;
  const pendingNewEmail = authUser?.new_email || emailSentTo;

  const submitEmailChange = async () => {
    if (emailChangeBusy) return;
    const next = newEmailInput.trim().toLowerCase();
    const current = (authUser?.email || user.email || '').toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) { setEmailChangeMsg('That does not look like an email address.'); return; }
    if (next === current) { setEmailChangeMsg('That is already your account email.'); return; }
    setEmailChangeBusy(true);
    setEmailChangeMsg('');
    const { data, error: err } = await supabase.auth.updateUser({ email: next });
    setEmailChangeBusy(false);
    if (err) {
      // Characterized on staging 2026-08-18: an already-registered target
      // surfaces as an explicit 422 email_exists (this authenticated endpoint
      // is not obfuscated by enumeration protection). Per the standing auth
      // rule the success path below stays truthful even if that ever changes.
      if (err.code === 'email_exists') setEmailChangeMsg('That address already has a CollectionKeeper account. Use a different address, or sign in to that account instead.');
      else if (err.status === 429) setEmailChangeMsg('Email sending is briefly rate-limited. Wait a few minutes and try again.');
      else if (err.code === 'email_address_invalid') setEmailChangeMsg('That address was rejected. Double-check the spelling and try again.');
      else setEmailChangeMsg('Could not start the change. Check your connection and try again.');
      return;
    }
    if (data?.user) setAuthUser(data.user);
    setEmailSentTo(next);
    localStorage.setItem(EMAIL_CHANGE_FROM_KEY, current);
    setEmailResendCooldown(EMAIL_RESEND_COOLDOWN_S);
    setEmailPanelOpen(false);
    setNewEmailInput('');
  };

  const resendEmailChange = async () => {
    if (emailResendCooldown > 0 || emailChangeBusy || !pendingNewEmail) return;
    setEmailChangeBusy(true);
    const { error: err } = await supabase.auth.resend({ type: 'email_change', email: pendingNewEmail });
    setEmailChangeBusy(false);
    if (err) setEmailChangeMsg(err.status === 429 ? 'Email sending is briefly rate-limited. Wait a few minutes and try again.' : 'Could not resend. Try again.');
    else { setEmailChangeMsg(''); setEmailResendCooldown(EMAIL_RESEND_COOLDOWN_S); }
  };

  const dismissContactEmailPrompt = () => {
    setContactEmailPrompt(false);
    localStorage.removeItem(EMAIL_CHANGE_FROM_KEY);
  };

  const applyContactEmailUpdate = async () => {
    const { error: err } = await supabase.from('profiles').update({ contact_email: accountEmail }).eq('id', user.id);
    if (err) { setEmailChangeMsg('Could not update the contact email. Try again.'); return; }
    setContactEmail(accountEmail);
    dismissContactEmailPrompt();
  };

  // ─── Collection export (COLLECTION STATS card) ────────────────────
  const runExport = useCallback(async (format) => {
    if (exporting) return;
    setExporting(format);
    setExportDone(null);
    setExportError('');
    try {
      if (format === 'xlsx') await exportCollectionXlsx(user, username);
      else await exportCollectionCsv(user, username);
      setExportDone(format);
      setTimeout(() => setExportDone(null), 3000);
    } catch (e) {
      setExportError(`Export failed — ${e.message || 'something went wrong'}. Check your connection and try again.`);
    } finally {
      setExporting(null);
    }
  }, [exporting, user, username]);

  // Collection stats for the COLLECTION STATS card — also re-run after an
  // import or import-undo changes the collection under the card.
  const reloadStats = useCallback(async () => {
    const { data: rows } = await supabase
      .from('collections')
      .select('status, grading_grade, card_key')
      .eq('user_id', user.id);
    if (!rows) return;
    const sets = new Set(rows.map(r => r.card_key.split('__')[0]));
    const { count: completedSales } = await supabase.from('transactions')
      .select('*', { count: 'exact', head: true }).eq('seller_id', user.id);
    const { count: completedBuys } = await supabase.from('transactions')
      .select('*', { count: 'exact', head: true }).eq('buyer_id', user.id);
    setStats({
      collected: rows.filter(r => r.status === 'collected').length,
      wishlist: rows.filter(r => r.status === 'wishlist').length,
      graded: rows.filter(r => r.grading_grade).length,
      sets: sets.size,
      completedSales: completedSales || 0,
      completedBuys: completedBuys || 0,
    });
  }, [user.id]);

  // ─── Initial load ──────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      if (profile) {
        setUsername(profile.username || '');
        setContactEmail(profile.contact_email || user.email || '');
        setIsPublic(profile.is_public || false);
        setShareHistory(profile.share_transaction_history_publicly || false);
        setIdentityVerified(!!profile.identity_verified);
        setVerifiedAt(profile.verified_at || null);
        setVerificationAttempts(profile.verification_attempts || 0);
        setVerificationLockedUntil(profile.verification_locked_until || null);

        setBio(profile.bio || '');
        setAvatarUrl(profile.avatar_url || null);

        setLegalFirstName(profile.legal_first_name || '');
        setLegalLastName(profile.legal_last_name || '');

        setAddrLine1(profile.shipping_address_line1 || '');
        setAddrLine2(profile.shipping_address_line2 || '');
        setAddrCity(profile.shipping_city || '');
        setAddrState(profile.shipping_state || '');
        setAddrPostal(profile.shipping_postal_code || '');
        setAddrCountry(profile.shipping_country || 'US');
        setAddrPhone(profile.shipping_phone || '');

        setAcceptsPaypal(!!profile.accepts_paypal);
        setPaypalEmail(profile.paypal_email || '');
        setAcceptsVenmo(!!profile.accepts_venmo);
        setVenmoUsername(profile.venmo_username || '');

        if (profile.created_at) {
          const d = new Date(profile.created_at);
          setMemberSince(d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
        }
      }
      // Load stats
      await reloadStats();
    })();
  }, [user.id, user.email, reloadStats]);

  // ─── Phase 2E.1: trust stats + history for MARKETPLACE TRUST card ──
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      const { data: stats } = await supabase
        .from('user_trust_stats')
        .select('role, total_transactions, badge_color, clean_count, unresolved_count, flagged_count')
        .eq('user_id', user.id);
      if (cancelled) return;
      const seller = stats?.find(s => s.role === 'seller');
      const buyer = stats?.find(s => s.role === 'buyer');
      setSellerStats(seller ? {
        count: seller.total_transactions, color: seller.badge_color,
        clean: seller.clean_count, unresolved: seller.unresolved_count, flagged: seller.flagged_count,
      } : null);
      setBuyerStats(buyer ? {
        count: buyer.total_transactions, color: buyer.badge_color,
        clean: buyer.clean_count, unresolved: buyer.unresolved_count, flagged: buyer.flagged_count,
      } : null);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    (async () => {
      const { data, error: invErr } = await supabase.functions.invoke('marketplace-trust-history', {
        body: { userId: user.id, role: 'both' },
      });
      if (cancelled) return;
      if (invErr) {
        console.error('[ProfilePage] trust history fetch failed:', invErr.message || invErr);
        setHistoryError('Could not load transaction history. Try refreshing.');
      } else {
        setHistoryData(data || { seller: { count: 0, rows: [] }, buyer: { count: 0, rows: [] } });
      }
      setHistoryLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // ─── Save ──────────────────────────────────────────────────────────
  const save = async () => {
    setError('');
    if (!username.trim()) { setError('Username cannot be empty.'); return; }
    if (bio.length > BIO_MAX) { setError(`Bio is too long (max ${BIO_MAX} characters).`); return; }

    // Lightweight email format check for PayPal email (only when method is on)
    if (acceptsPaypal && paypalEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(paypalEmail.trim())) {
      setError('PayPal email looks invalid.'); return;
    }

    setSaving(true);
    // Check username uniqueness if changed. Best-effort via the public view
    // (private profiles aren't visible); the UNIQUE constraint is the backstop.
    const { data: existing } = await supabase
      .from('public_profiles')
      .select('id')
      .eq('username', username.trim())
      .neq('id', user.id)
      .maybeSingle();
    if (existing) { setError('That username is already taken.'); setSaving(false); return; }

    const payload = {
      username: username.trim(),
      contact_email: contactEmail.trim(),
      is_public: isPublic,
      bio: bio.trim() || null,
      legal_first_name: legalFirstName.trim() || null,
      legal_last_name: legalLastName.trim() || null,
      shipping_address_line1: addrLine1.trim() || null,
      shipping_address_line2: addrLine2.trim() || null,
      shipping_city: addrCity.trim() || null,
      shipping_state: addrState.trim() || null,
      shipping_postal_code: addrPostal.trim() || null,
      shipping_country: addrCountry || 'US',
      shipping_phone: addrPhone.trim() || null,
      accepts_paypal: acceptsPaypal,
      paypal_email: acceptsPaypal ? (paypalEmail.trim() || null) : null,
      accepts_venmo: acceptsVenmo,
      // Strip leading @ from Venmo handle to normalize storage.
      venmo_username: acceptsVenmo ? (venmoUsername.trim().replace(/^@/, '') || null) : null,
    };

    const { error: err } = await supabase
      .from('profiles')
      .update(payload)
      .eq('id', user.id);
    if (err) {
      setSaving(false);
      setError('Save failed. Please try again.');
      return;
    }

    // Phase 2E.2 § 6.4 edge case — payment-method readiness drives whether
    // the seller's for-sale listings stay visible:
    //   - Neither method ready    → auto-PAUSE all active for-sale listings.
    //                               They'd otherwise be public but un-buyable.
    //   - At least one ready      → auto-UNPAUSE the listings that were paused
    //                               for this reason. We leave Phase 2E.1's
    //                               $500-verification pauses alone (sale_price
    //                               > 500 AND seller unverified — those need
    //                               Persona, not a payment method).
    const paypalReady = !!payload.accepts_paypal && !!payload.paypal_email;
    const venmoReady  = !!payload.accepts_venmo  && !!payload.venmo_username;
    const VERIFICATION_GATE_THRESHOLD = 500;
    setPausedNotice('');
    if (!paypalReady && !venmoReady) {
      const { data: paused, error: pauseErr } = await supabase
        .from('collections')
        .update({ is_paused: true })
        .eq('user_id', user.id)
        .eq('status', 'for_sale')
        .or('is_paused.is.null,is_paused.eq.false')
        .select('card_key');
      if (pauseErr) {
        console.warn('[profile-save] auto-pause failed:', pauseErr.message);
      } else if (paused && paused.length > 0) {
        const n = paused.length;
        setPausedNotice(
          `Paused ${n} for-sale ${n === 1 ? 'listing' : 'listings'} — ` +
          'they\'ll come back to the marketplace as soon as you add a PayPal email or Venmo handle.'
        );
      }
    } else {
      // At least one payment method is now ready. Unpause any for-sale
      // listings that don't ALSO need verification gating.
      //
      // Never resurrect a card that's mid-sale. is_paused is overloaded
      // (payment-method pause / $500-verification pause / SOLD pause set by
      // the PayPal+escrow webhooks and the Venmo handler), so a blanket
      // unpause would re-list cards that sold. Exclude card_keys with a live
      // transaction — any status except payment_pending (an unfunded/abandoned
      // Buy). A proper sticky "sold" state is roadmapped (2.1, Gap #9).
      const { data: liveTxns } = await supabase
        .from('transactions')
        .select('card_key')
        .eq('seller_id', user.id)
        .neq('status', 'payment_pending');
      const soldKeys = Array.from(new Set((liveTxns || []).map((t) => t.card_key).filter(Boolean)));

      let unpauseQ = supabase
        .from('collections')
        .update({ is_paused: false })
        .eq('user_id', user.id)
        .eq('status', 'for_sale')
        .eq('is_paused', true);
      if (soldKeys.length > 0) {
        unpauseQ = unpauseQ.not('card_key', 'in', `(${soldKeys.map((k) => `"${k}"`).join(',')})`);
      }
      if (!identityVerified) {
        // Seller still unverified: leave $500+ listings paused (Phase 2E.1 lock).
        unpauseQ = unpauseQ.lte('sale_price', VERIFICATION_GATE_THRESHOLD);
      }
      const { data: unpaused, error: unpauseErr } = await unpauseQ.select('card_key');
      if (unpauseErr) {
        console.warn('[profile-save] auto-unpause failed:', unpauseErr.message);
      } else if (unpaused && unpaused.length > 0) {
        const n = unpaused.length;
        setPausedNotice(
          `Restored ${n} for-sale ${n === 1 ? 'listing' : 'listings'} to the marketplace.`
        );
      }
    }

    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const signOut = async () => { await supabase.auth.signOut(); };

  // ─── Avatar upload ─────────────────────────────────────────────────
  const handleAvatarClick = () => {
    if (avatarFileInputRef.current) avatarFileInputRef.current.click();
  };

  const handleAvatarFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    e.target.value = ''; // allow re-selecting the same file later

    if (!file.type.startsWith('image/')) {
      setError('Avatar must be an image file.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Avatar image is too large (max 8MB before resize).');
      return;
    }

    setError('');
    setAvatarUploading(true);
    try {
      const blob = await resizeAvatarSquare(file);
      const path = `${user.id}/avatar.jpg`;
      const { error: upErr } = await supabase.storage
        .from('profile-avatars')
        .upload(path, blob, { upsert: true, contentType: 'image/jpeg' });
      if (upErr) throw new Error(upErr.message);

      const { data: pub } = supabase.storage.from('profile-avatars').getPublicUrl(path);
      const publicUrl = pub?.publicUrl || null;

      const { error: profErr } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id);
      if (profErr) throw new Error(profErr.message);

      setAvatarUrl(publicUrl);
      setAvatarCacheBust(Date.now()); // bust browser cache so the new image shows immediately
    } catch (err) {
      console.error('[avatar upload]', err);
      setError('Avatar upload failed. Try a different image.');
    } finally {
      setAvatarUploading(false);
    }
  };

  // ─── Persona verification flow ─────────────────────────────────────
  const pollForVerification = useCallback(async () => {
    const start = Date.now();
    while (Date.now() - start < POLL_TIMEOUT_MS) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      const { data } = await supabase
        .from('profiles')
        .select('identity_verified,verified_at,verification_attempts,verification_locked_until')
        .eq('id', user.id)
        .maybeSingle();
      if (data?.identity_verified) {
        setIdentityVerified(true);
        setVerifiedAt(data.verified_at);
        setVerificationAttempts(data.verification_attempts || 0);
        setVerificationLockedUntil(data.verification_locked_until || null);
        setVerifying(false);
        setVerifyToast('');
        return;
      }
      if (data) {
        setVerificationAttempts(data.verification_attempts || 0);
        setVerificationLockedUntil(data.verification_locked_until || null);
      }
    }
    setVerifying(false);
    setVerifyToast('Verification submitted. Refresh the page in a moment to see your status.');
  }, [user.id]);

  const launchPersonaFlow = useCallback(() => {
    if (!PERSONA_TEMPLATE_ID) {
      setVerifyToast('Identity verification is not configured for this environment. Email keeper18@collectionkeeper.org.');
      return;
    }
    setVerifyToast('');
    setVerifying(true);

    // Pre-fill name fields when present — stronger ID match + lower friction in the Persona flow.
    const prefillFields = {};
    if (legalFirstName.trim()) prefillFields.nameFirst = legalFirstName.trim();
    if (legalLastName.trim()) prefillFields.nameLast = legalLastName.trim();

    const client = new Persona.Client({
      templateId: PERSONA_TEMPLATE_ID,
      ...(PERSONA_ENVIRONMENT_ID ? { environmentId: PERSONA_ENVIRONMENT_ID } : {}),
      referenceId: user.id,
      ...(Object.keys(prefillFields).length ? { fields: prefillFields } : {}),
      onReady: () => client.open(),
      onComplete: () => { pollForVerification(); },
      onCancel: () => { setVerifying(false); },
      onError: (err) => {
        console.error('[Persona] flow error:', err);
        setVerifying(false);
        setVerifyToast('Verification ran into a problem. Try again, or email keeper18@collectionkeeper.org.');
      },
    });
    personaClientRef.current = client;
  }, [user.id, pollForVerification, legalFirstName, legalLastName]);

  // Render-state derivations
  const isLocked = !identityVerified
    && verificationAttempts >= HARD_FAIL_THRESHOLD
    && verificationLockedUntil
    && new Date(verificationLockedUntil) > new Date();
  const verifiedDateStr = verifiedAt
    ? new Date(verifiedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const lockedUntilStr = verificationLockedUntil
    ? new Date(verificationLockedUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const avatarSrc = avatarUrl ? `${avatarUrl}${avatarCacheBust ? `?t=${avatarCacheBust}` : ''}` : null;
  const charsLeftClass = bio.length > BIO_MAX ? S.bioCharCountOver : S.bioCharCount;

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <NavDrawer user={user} />

      <header style={S.header}>
        <div style={S.hInner}>
          <div>
            <div style={S.logoLine}>COLLECTIONKEEPER · ACCOUNT</div>
            <h1 style={S.title}>MY <span style={{ color: 'var(--brand-gold)' }}>PROFILE</span></h1>
          </div>
          <button onClick={onBack} style={S.backBtn}>← MY CHECKLISTS</button>
        </div>
        <div style={{ height: '1px', background: 'linear-gradient(90deg,transparent,var(--cyan),var(--brand-gold),var(--cyan),transparent)' }} />
      </header>

      <main style={S.main}>

        {/* Color key — onboarding cue for the section-color system */}
        <div style={S.colorKey}>
          {[
            ['var(--cyan)', 'PUBLIC IDENTITY'],
            ['var(--gold)', 'ACCOUNT META'],
            ['var(--purple)', 'PRIVATE SETUP'],
            ['var(--ok-2)', 'TRUST'],
          ].map(([swatch, label]) => (
            <div key={label} style={S.colorKeyItem}>
              <span style={{ ...S.colorKeySwatch, background: swatch }} />
              {label}
            </div>
          ))}
        </div>

        {/* ─── CYAN · Avatar + Bio ────────────────────────────────── */}
        <div style={{ ...S.card, ...S.sectionCyan, ...S.avatarCard }}>
          <div style={S.avatarEditor}>
            <button
              type="button"
              onClick={handleAvatarClick}
              style={{
                ...S.avatarCircle,
                ...(avatarSrc ? S.avatarCircleHasImage : {}),
                opacity: avatarUploading ? 0.6 : 1,
              }}
              aria-label={avatarSrc ? 'Change avatar' : 'Add avatar'}
              title={avatarSrc ? 'Tap to change avatar' : 'Tap to add avatar'}
              disabled={avatarUploading}
            >
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt="Your avatar"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                />
              ) : (
                (username.charAt(0) || '?').toUpperCase()
              )}
            </button>
            <span
              role="presentation"
              style={S.avatarEditOverlay}
              onClick={handleAvatarClick}
              title={avatarSrc ? 'Change avatar' : 'Add avatar'}
            >{avatarUploading ? '…' : '✎'}</span>
            <input
              ref={avatarFileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleAvatarFile}
            />
          </div>
          <div style={S.avatarInfo}>
            <div style={S.bigNameRow}>
              <div style={S.bigName}>{username || 'Your Profile'}</div>
              {identityVerified && (
                <span style={S.verifiedChip} title="Identity verified by Persona">✓ VERIFIED</span>
              )}
            </div>
            <div style={S.metaLine}>
              {isPublic ? '◉ PUBLIC PROFILE' : '◎ PRIVATE PROFILE'}
              {memberSince ? ` · MEMBER SINCE ${memberSince.toUpperCase()}` : ''}
            </div>
            {isPublic && username && (
              <a href={`/u/${username}`} target="_blank" rel="noreferrer" style={S.publicLink}>
                VIEW PUBLIC PAGE ↗
              </a>
            )}
            <div style={S.bioBlock}>
              <div style={S.bioLabelRow}>
                <label style={S.bioLabel}>BIO</label>
                <span style={charsLeftClass}>{bio.length} / {BIO_MAX}</span>
              </div>
              <textarea
                style={S.bioTextarea}
                value={bio}
                onChange={(e) => setBio(e.target.value.slice(0, BIO_MAX + 50))}
                placeholder="A few lines about what you collect, what you're chasing, what you're done with. Shown on your public profile."
                rows={3}
              />
              <div style={{ ...S.hint, marginTop: 6 }}>Shown on your public profile under your handle. Optional. Up to {BIO_MAX} characters.</div>
            </div>
          </div>
        </div>

        {/* ─── GOLD · Account + Visibility (2-col) ────────────────── */}
        <div style={S.grid}>
          <div style={{ ...S.card, ...S.sectionGold }}>
            <div style={{ ...S.cardTitle, ...S.cardTitleGold }}>ACCOUNT INFO</div>
            <div style={S.field}>
              <label style={S.label}>USERNAME</label>
              <input
                style={S.input}
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="your username..."
                maxLength={30}
              />
              <div style={S.hint}>Letters, numbers, underscores only. Max 30 chars.</div>
            </div>
            <div style={S.field}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <label style={S.label}>ACCOUNT EMAIL</label>
                {!emailPanelOpen && !pendingNewEmail && (
                  <button style={S.emailChangeBtn} onClick={() => { setEmailPanelOpen(true); setEmailChangeMsg(''); }}>CHANGE</button>
                )}
              </div>
              <div style={{ ...S.input, color: 'var(--muted-5)', cursor: 'default' }}>{accountEmail}</div>
              <div style={S.hint}>The address you sign in with.</div>

              {emailPanelOpen && !pendingNewEmail && (
                <div style={S.emailChangeBox}>
                  <input
                    style={S.input}
                    type="email"
                    value={newEmailInput}
                    onChange={e => setNewEmailInput(e.target.value)}
                    placeholder="new-address@example.com"
                    autoFocus
                  />
                  <div style={{ ...S.hint, marginTop: '8px', lineHeight: 1.6 }}>
                    We&apos;ll send confirmation links to both your current and your new address. The change happens once both are clicked.
                  </div>
                  {emailChangeMsg && <div style={S.emailChangeMsg}>{emailChangeMsg}</div>}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <button
                      style={{ ...S.emailChangeSubmit, ...(emailChangeBusy ? S.emailChangeDisabled : {}) }}
                      disabled={emailChangeBusy}
                      onClick={submitEmailChange}
                    >
                      {emailChangeBusy ? 'SENDING…' : 'SEND CONFIRMATION LINKS'}
                    </button>
                    <button
                      style={S.emailChangeCancel}
                      onClick={() => { setEmailPanelOpen(false); setNewEmailInput(''); setEmailChangeMsg(''); }}
                    >CANCEL</button>
                  </div>
                </div>
              )}

              {pendingNewEmail && (
                <div style={S.emailChangeBox}>
                  <div style={S.emailPendingLine}>// EMAIL CHANGE PENDING //</div>
                  <div style={S.emailPendingTarget}>{accountEmail} → {pendingNewEmail}</div>
                  <div style={{ ...S.hint, lineHeight: 1.6 }}>
                    Confirmation links went to both addresses — the change completes once both are clicked.
                    Didn&apos;t get them? Check spam, or resend. Unclicked links simply expire and nothing changes.
                  </div>
                  {emailChangeMsg && <div style={S.emailChangeMsg}>{emailChangeMsg}</div>}
                  <button
                    style={{ ...S.emailChangeSubmit, marginTop: '10px', ...(emailResendCooldown > 0 || emailChangeBusy ? S.emailChangeDisabled : {}) }}
                    disabled={emailResendCooldown > 0 || emailChangeBusy}
                    onClick={resendEmailChange}
                  >
                    {emailChangeBusy ? 'SENDING…' : emailResendCooldown > 0 ? `RESEND EMAILS (${emailResendCooldown}S)` : 'RESEND EMAILS'}
                  </button>
                </div>
              )}

              {contactEmailPrompt && !pendingNewEmail && (
                <div style={S.emailChangeBox}>
                  <div style={{ ...S.hint, lineHeight: 1.6 }}>
                    Your account email changed, but your Marketplace contact email still points at the old address.
                    Update it to {accountEmail}?
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                    <button style={S.emailChangeSubmit} onClick={applyContactEmailUpdate}>UPDATE CONTACT EMAIL</button>
                    <button style={S.emailChangeCancel} onClick={dismissContactEmailPrompt}>KEEP AS IS</button>
                  </div>
                </div>
              )}
            </div>
            <div style={S.field}>
              <label style={S.label}>CONTACT EMAIL</label>
              <div style={{ ...S.input, color: 'var(--muted-5)', cursor: 'default' }}>{contactEmail || user.email}</div>
              <div style={S.hint}>Private. Never shown on your profile. Used only for Marketplace sale emails.</div>
            </div>
          </div>

          <div style={{ ...S.card, ...S.sectionGold }}>
            <div style={{ ...S.cardTitle, ...S.cardTitleGold }}>VISIBILITY</div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: '16px' }}>
              <div>
                <div style={{ fontFamily: "'Rajdhani',sans-serif", fontWeight: 600, fontSize: '15px', color: 'var(--text)', marginBottom: '4px' }}>Public Profile</div>
                <div style={S.hint}>When on, your collection appears in Community Checklists and your listings appear in the Marketplace.</div>
              </div>
              <button
                onClick={() => setIsPublic(p => !p)}
                style={{ ...S.toggle, background: isPublic ? 'rgba(var(--gold-rgb),0.22)' : 'rgba(var(--slate-rgb),0.4)', borderColor: isPublic ? 'rgba(var(--gold-rgb),0.55)' : 'var(--muted-2)', flexShrink: 0 }}
              >
                <div style={{ ...S.toggleKnob, transform: isPublic ? 'translateX(18px)' : 'translateX(2px)', background: isPublic ? 'var(--gold)' : 'var(--muted-5)' }} />
              </button>
            </div>
            <div style={{ padding: '12px', background: isPublic ? 'rgba(var(--gold-rgb),0.06)' : 'rgba(var(--slate-rgb),0.2)', border: `1px solid ${isPublic ? 'rgba(var(--gold-rgb),0.25)' : 'var(--border)'}`, borderRadius: '2px' }}>
              <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '12px', color: isPublic ? 'var(--gold)' : 'var(--muted-5)' }}>
                {isPublic
                  ? '✓ Your collection is visible to the community'
                  : '○ Your collection is private'}
              </div>
            </div>
            {/* APPEARANCE — applies live so the page itself is the preview */}
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
              <label style={S.label}>APPEARANCE</label>
              <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                <button
                  onClick={() => chooseTheme('dark')}
                  style={{ flex: 1, fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '1px', padding: '9px', borderRadius: '2px', cursor: 'pointer',
                    border: `1px solid ${theme !== 'light' ? 'var(--red)' : 'var(--border)'}`,
                    color: theme !== 'light' ? 'var(--red)' : 'var(--muted-2)',
                    background: theme !== 'light' ? 'rgba(var(--red-rgb),0.08)' : 'transparent' }}
                >⬛ DARK SIDE</button>
                <button
                  onClick={() => chooseTheme('light')}
                  style={{ flex: 1, fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '1px', padding: '9px', borderRadius: '2px', cursor: 'pointer',
                    border: `1px solid ${theme === 'light' ? 'var(--cyan)' : 'var(--border)'}`,
                    color: theme === 'light' ? 'var(--cyan)' : 'var(--muted-2)',
                    background: theme === 'light' ? 'rgba(var(--cyan-rgb),0.08)' : 'transparent' }}
                >⬜ LIGHT SIDE</button>
              </div>
              <div style={{ ...S.hint, marginTop: '8px', lineHeight: 1.6 }}>
                {theme === 'light'
                  ? 'Light Side is a bright, high-contrast theme with larger text for improved readability and accessibility. Changes apply across CollectionKeeper.'
                  : 'Dark Side is the classic deep-space look. Choose Light Side for a bright, high-contrast theme with larger text — easier to read, especially on phones. Changes apply across CollectionKeeper.'}
              </div>
            </div>
          </div>
        </div>

        {/* ─── PURPLE · TRANSACTION SETUP section ─────────────────── */}
        <div style={S.sectionHeader}>// TRANSACTION SETUP //</div>

        {/* LEGAL NAME */}
        <div style={{ ...S.card, ...S.sectionPurple }}>
          <div style={S.cardTitleRow}>
            <div style={{ ...S.cardTitle, ...S.cardTitlePurple, margin: 0, padding: 0, border: 'none' }}>LEGAL NAME</div>
            <span style={S.privateBadge}>PRIVATE</span>
          </div>
          <div style={S.fieldRow2}>
            <div style={S.field}>
              <label style={S.label}>FIRST NAME</label>
              <input style={S.input} value={legalFirstName} onChange={(e) => setLegalFirstName(e.target.value)} placeholder="e.g. Tomás" maxLength={60} />
            </div>
            <div style={S.field}>
              <label style={S.label}>LAST NAME</label>
              <input style={S.input} value={legalLastName} onChange={(e) => setLegalLastName(e.target.value)} placeholder="e.g. Bilbao" maxLength={60} />
            </div>
          </div>
          <div style={S.hintInfo}>
            Used only to verify your identity with Persona and on shipping labels. Never shown publicly. Never shared with collectors you haven't transacted with.
          </div>
        </div>

        {/* SHIPPING ADDRESS */}
        <div style={{ ...S.card, ...S.sectionPurple }}>
          <div style={S.cardTitleRow}>
            <div style={{ ...S.cardTitle, ...S.cardTitlePurple, margin: 0, padding: 0, border: 'none' }}>SHIPPING ADDRESS</div>
            <span style={S.privateBadge}>PRIVATE</span>
          </div>
          <div style={S.field}>
            <label style={S.label}>ADDRESS LINE 1</label>
            <input style={S.input} value={addrLine1} onChange={(e) => setAddrLine1(e.target.value)} placeholder="e.g. 1234 Bantha Trail" maxLength={120} />
          </div>
          <div style={S.field}>
            <label style={S.label}>ADDRESS LINE 2 <span style={S.labelOptional}>(OPTIONAL)</span></label>
            <input style={S.input} value={addrLine2} onChange={(e) => setAddrLine2(e.target.value)} placeholder="e.g. Apt 4B" maxLength={120} />
          </div>
          <div style={S.fieldRow3}>
            <div style={S.field}>
              <label style={S.label}>CITY</label>
              <input style={S.input} value={addrCity} onChange={(e) => setAddrCity(e.target.value)} placeholder="e.g. Los Angeles" maxLength={80} />
            </div>
            <div style={S.field}>
              <label style={S.label}>STATE</label>
              <input style={S.input} value={addrState} onChange={(e) => setAddrState(e.target.value)} placeholder="CA" maxLength={40} />
            </div>
            <div style={S.field}>
              <label style={S.label}>POSTAL CODE</label>
              <input style={S.input} value={addrPostal} onChange={(e) => setAddrPostal(e.target.value)} placeholder="90001" maxLength={20} />
            </div>
          </div>
          <div style={S.fieldRow2}>
            <div style={S.field}>
              <label style={S.label}>COUNTRY</label>
              <select style={S.input} value={addrCountry} onChange={(e) => setAddrCountry(e.target.value)}>
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>PHONE <span style={S.labelOptional}>(OPTIONAL)</span></label>
              <input style={S.input} value={addrPhone} onChange={(e) => setAddrPhone(e.target.value)} placeholder="e.g. +1 555 0123" maxLength={30} />
            </div>
          </div>
          <div style={S.hintInfo}>
            Shared only with the collectors you transact with, at the moment a sale or trade starts. Never on your public profile. Required by carriers for shipping labels. Phone helps with carriers that require it (UPS Next Day Air, FedEx Priority).
          </div>
        </div>

        {/* PAYMENT METHODS */}
        <div style={{ ...S.card, ...S.sectionPurple }}>
          <div style={S.cardTitleRow}>
            <div style={{ ...S.cardTitle, ...S.cardTitlePurple, margin: 0, padding: 0, border: 'none' }}>PAYMENT METHODS</div>
            <span style={S.privateBadge}>PRIVATE</span>
          </div>

          <PaymentRow
            on={acceptsPaypal}
            onToggle={() => setAcceptsPaypal(v => !v)}
            logoStyle={{ ...S.payLogo, background: '#003087', color: 'white' }}
            logoText="PayPal"
            value={paypalEmail}
            onChange={setPaypalEmail}
            placeholder="your-paypal-email@example.com"
          />
          <div style={{ ...S.hintInfo, margin: '0 0 14px 60px' }}>
            Buyers can send G&amp;S (buyer protection) to this address. Your PayPal email is never shown — CK generates a payment link server-side.
          </div>

          <PaymentRow
            on={acceptsVenmo}
            onToggle={() => setAcceptsVenmo(v => !v)}
            logoStyle={{ ...S.payLogo, background: '#3D95CE', color: 'white' }}
            logoText="Venmo"
            value={venmoUsername}
            onChange={setVenmoUsername}
            placeholder="@your-venmo-business-handle"
          />
          <div style={{ ...S.hintWarn, margin: '0 0 8px 60px' }}>
            ⚠ Your Venmo handle is visible to buyers when they tap Buy with Venmo. Use a Venmo Business handle, not a personal one.
          </div>

          <div style={{ ...S.hintInfo, marginTop: 14, paddingTop: 12, borderTop: '0.5px solid var(--wash-06)' }}>
            At least one method is required to list cards For Sale (trades work without). Buyers see only the methods you accept — no Venmo button shows up if you don't accept Venmo. Absence is the signal.
          </div>
        </div>

        {/* ─── GREEN · Identity verification ─────────────────────── */}
        <div style={{ ...S.card, ...S.sectionGreen }}>
          <div style={{ ...S.cardTitle, ...S.cardTitleGreen }}>IDENTITY VERIFICATION</div>

          {identityVerified ? (
            <div style={S.verifiedBlock}>
              <div style={S.verifiedRow}>
                <span style={S.verifiedPill}>✓ VERIFIED</span>
                <span style={S.verifiedDate}>
                  {verifiedDateStr ? `verified ${verifiedDateStr}` : 'verified'}
                </span>
              </div>
              <div style={S.verifiedBody}>
                Identity verified. Eligible to list cards at any price, including listings over $500 that require escrow.
              </div>
            </div>
          ) : isLocked ? (
            <div style={S.lockedBlock}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={S.lockedIcon}>⚠</span>
                <span style={S.lockedHeader}>Verification temporarily locked</span>
              </div>
              <div style={S.lockedBody}>
                We couldn't verify your identity after 3 attempts. Your collection is safe — browsing and listing under $500 still works.
                {lockedUntilStr && <> You can try again after {lockedUntilStr}.</>}
              </div>
              <a href="mailto:keeper18@collectionkeeper.org?subject=Verification%20issue" style={S.emailKeeperBtn}>
                EMAIL KEEPER18 DIRECTLY
              </a>
            </div>
          ) : verifying ? (
            <div style={S.verifyingBlock}>
              <span style={S.verifyingSpinner}>◐</span>
              <span style={S.verifyingText}>Verifying — keep this page open. Usually a few seconds.</span>
            </div>
          ) : (
            <>
              <div style={S.verifyIntro}>
                Voluntary. Required only for listings over $500.{' '}
                Verified collectors show a <strong style={{ color: 'var(--ok)' }}>✓ VERIFIED</strong> pill on every listing — a positive trust signal in the marketplace.
              </div>
              <button
                onClick={launchPersonaFlow}
                disabled={!PERSONA_TEMPLATE_ID}
                style={{ ...S.getVerifiedBtn, opacity: PERSONA_TEMPLATE_ID ? 1 : 0.5, cursor: PERSONA_TEMPLATE_ID ? 'pointer' : 'not-allowed' }}
              >
                GET VERIFIED →
              </button>
              {!PERSONA_TEMPLATE_ID && (
                <div style={{ ...S.hint, marginTop: 8, color: 'var(--err)' }}>
                  Identity verification isn't configured in this environment yet.
                </div>
              )}
              <div style={{ ...S.hint, marginTop: 10 }}>
                CK never sees your ID. Verified by Persona. About 3 minutes.
              </div>
              {verificationAttempts > 0 && verificationAttempts < HARD_FAIL_THRESHOLD && (
                <div style={{ ...S.hint, marginTop: 8, color: 'var(--gold)' }}>
                  Last attempt didn't verify ({verificationAttempts} of {HARD_FAIL_THRESHOLD}). Try again with a clearer photo.
                </div>
              )}
            </>
          )}

          {verifyToast && (
            <div style={{ ...S.hint, marginTop: 10, color: 'var(--brand-gold)' }}>{verifyToast}</div>
          )}
        </div>

        {/* ─── GREEN · Marketplace trust (badges + breakdown + scrollable history + explainer) ─── */}
        <MarketplaceTrustCard
          sellerStats={sellerStats}
          buyerStats={buyerStats}
          historyData={historyData}
          historyLoading={historyLoading}
          historyError={historyError}
          trustTab={trustTab}
          setTrustTab={setTrustTab}
          shareHistory={shareHistory}
          shareHistorySaving={shareHistorySaving}
          onToggleShareHistory={toggleShareHistory}
        />

        {/* ─── CYAN · Collection stats ────────────────────────────── */}
        <div style={{ ...S.card, ...S.sectionCyan }}>
          <div style={{ ...S.cardTitle, ...S.cardTitleCyan }}>COLLECTION STATS</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', textAlign: 'center' }}>
            {[
              { val: stats.collected, label: 'COLLECTED', color: 'var(--cyan)' },
              { val: stats.wishlist, label: 'WISHLIST', color: 'var(--gold)' },
              { val: stats.graded, label: 'GRADED', color: 'var(--brand-gold)' },
              { val: stats.sets, label: 'SETS', color: 'var(--purple)' },
              { val: stats.completedSales || 0, label: 'SALES DONE', color: 'var(--ok)' },
              { val: stats.completedBuys || 0, label: 'BUYS DONE', color: 'var(--ok)' },
            ].map(({ val, label, color }) => (
              <div key={label}>
                <div style={{ fontFamily: "'Orbitron',monospace", fontSize: '24px', fontWeight: 700, color }}>{val}</div>
                <div style={S.statLabel}>{label}</div>
              </div>
            ))}
          </div>

          {/* Export strip — download the whole collection as a spreadsheet */}
          <div style={S.exportStrip}>
            <div style={S.exportHint}>
              Download a spreadsheet copy of everything you've logged — cards, parallels, serials, grading, prices, photo links.
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => runExport('xlsx')}
                disabled={!!exporting}
                style={{
                  ...S.exportBtnPrimary,
                  ...(exporting ? S.exportBtnDisabled : {}),
                  ...(exportDone === 'xlsx' ? S.exportBtnDone : {}),
                }}
              >
                {exporting === 'xlsx' ? '◐ EXPORTING…' : exportDone === 'xlsx' ? '✓ DOWNLOADED' : '⬇ EXCEL (.XLSX)'}
              </button>
              <button
                onClick={() => runExport('csv')}
                disabled={!!exporting}
                style={{
                  ...S.exportBtnSecondary,
                  ...(exporting ? S.exportBtnDisabled : {}),
                  ...(exportDone === 'csv' ? S.exportBtnDone : {}),
                }}
              >
                {exporting === 'csv' ? '◐ EXPORTING…' : exportDone === 'csv' ? '✓ DOWNLOADED' : '⬇ CSV'}
              </button>
            </div>
            {exportError && <div style={{ ...S.errorMsg, marginTop: 10 }}>{exportError}</div>}

            {/* Import — upload the same spreadsheet format back (v2.4.66) */}
            <CollectionImport user={user} onDataChanged={reloadStats} />
          </div>
        </div>

        {/* Auto-pause notice (Phase 2E.2 § 6.4 edge case) — amber tone, not red.
            Not an error; just letting the seller know their listings stepped
            off the marketplace until a payment method is in place. */}
        {pausedNotice && (
          <div style={{
            padding: '10px 12px',
            background: 'rgba(234, 179, 8, 0.08)',
            border: '0.5px solid rgba(234, 179, 8, 0.4)',
            borderRadius: 3,
            color: 'var(--warn)',
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: 12,
            lineHeight: 1.5,
            marginBottom: 10,
          }}>💰 {pausedNotice}</div>
        )}

        {/* Error / Save */}
        {error && <div style={S.errorMsg}>{error}</div>}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={signOut} style={S.signOutBtn}>SIGN OUT</button>
          <button onClick={save} disabled={saving} style={{ ...S.saveBtn, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'SAVING...' : saved ? '✓ SAVED' : 'SAVE CHANGES'}
          </button>
        </div>

        <DeleteAccountSection />

      </main>
    </div>
  );
}

// ─── Inline subcomponent: payment row ────────────────────────────────
function PaymentRow({ on, onToggle, logoStyle, logoText, value, onChange, placeholder }) {
  return (
    <div style={{ ...S.payRow, opacity: on ? 1 : 0.6 }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          ...S.toggle,
          background: on ? 'rgba(var(--purple-rgb),0.22)' : 'rgba(var(--slate-rgb),0.4)',
          borderColor: on ? 'rgba(var(--purple-rgb),0.55)' : 'var(--muted-2)',
        }}
        aria-pressed={on}
        aria-label={`${logoText} accepted: ${on ? 'on' : 'off'}`}
      >
        <div style={{ ...S.toggleKnob, transform: on ? 'translateX(18px)' : 'translateX(2px)', background: on ? 'var(--purple)' : 'var(--muted-5)' }} />
      </button>
      <div style={S.payField}>
        <span style={logoStyle}>{logoText}</span>
        <input
          style={{ ...S.input, flex: 1 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={!on}
        />
      </div>
    </div>
  );
}

// ─── MarketplaceTrustCard ─────────────────────────────────────────────
// Phase 2E.1 addition. Shows seller + buyer badges + breakdown + scrollable
// transaction history + always-on explainer. Sprout state for new collectors.
function MarketplaceTrustCard({
  sellerStats, buyerStats, historyData, historyLoading, historyError, trustTab, setTrustTab,
  shareHistory, shareHistorySaving, onToggleShareHistory,
}) {
  const isSprout = !sellerStats && !buyerStats;

  return (
    <div style={{ ...S.card, ...S.sectionGreen }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ ...S.cardTitle, ...S.cardTitleGreen, marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>MARKETPLACE TRUST</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', whiteSpace: 'nowrap', color: shareHistory ? 'var(--ok)' : 'var(--muted-5)' }}>
            {shareHistory ? 'Showing @username' : 'Anonymous'}
          </span>
          <button
            type="button"
            onClick={onToggleShareHistory}
            disabled={shareHistorySaving}
            aria-pressed={shareHistory}
            title="When on, other collectors see your @username (instead of 'a CK collector') in their transaction history for deals with you. When off, you stay anonymous. Per-deal control is coming in 2.1."
            style={{ ...S.toggle, flexShrink: 0, background: shareHistory ? 'rgba(var(--ok-2-rgb),0.22)' : 'rgba(var(--slate-rgb),0.4)', borderColor: shareHistory ? 'rgba(var(--ok-2-rgb),0.55)' : 'var(--muted-2)', opacity: shareHistorySaving ? 0.6 : 1, cursor: shareHistorySaving ? 'default' : 'pointer' }}
          >
            <div style={{ ...S.toggleKnob, transform: shareHistory ? 'translateX(18px)' : 'translateX(2px)', background: shareHistory ? 'var(--ok)' : 'var(--muted-5)' }} />
          </button>
        </div>
      </div>

      <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', color: 'var(--muted-5)', lineHeight: 1.5, marginBottom: '14px' }}>
        Heads up: completed sales always show your @username and price in the public <span style={{ color: 'var(--ok)' }}>Sold</span> feed — this toggle only controls your name in other collectors&rsquo; transaction history.
      </div>

      {isSprout ? (
        <div style={S.trustSprout}>
          <div style={S.trustSproutIcon}>🌱</div>
          <div style={S.trustSproutTitle}>New collector</div>
          <div style={S.trustSproutSub}>
            Your marketplace reputation will appear here after your first completed transaction.
          </div>
        </div>
      ) : (
        <>
          <div style={S.trustBadgesRow}>
            <TrustBadgeCell stats={sellerStats} role="seller" />
            <TrustBadgeCell stats={buyerStats} role="buyer" />
          </div>

          <div style={S.trustTabs}>
            <button
              type="button"
              onClick={() => setTrustTab('seller')}
              style={{ ...S.trustTab, ...(trustTab === 'seller' ? S.trustTabActive : {}) }}
            >Seller history ({sellerStats?.count || 0})</button>
            <button
              type="button"
              onClick={() => setTrustTab('buyer')}
              style={{ ...S.trustTab, ...(trustTab === 'buyer' ? S.trustTabActive : {}) }}
            >Buyer history ({buyerStats?.count || 0})</button>
          </div>

          <div style={S.trustHistoryScroll}>
            {historyLoading ? (
              <div style={{ padding: '24px 8px', textAlign: 'center', color: 'var(--muted-5)', fontFamily: "'Share Tech Mono',monospace", fontSize: '11px' }}>
                Loading transaction history…
              </div>
            ) : historyError ? (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--err)', background: 'rgba(var(--err-rgb),0.06)', border: '0.5px solid rgba(var(--err-rgb),0.3)', borderRadius: 4, fontFamily: "'Share Tech Mono',monospace", fontSize: '12px' }}>
                {historyError}
              </div>
            ) : (() => {
              const rows = historyData?.[trustTab]?.rows || [];
              if (rows.length === 0) {
                return (
                  <div style={{ padding: '24px 8px', textAlign: 'center', color: 'var(--muted-6)', fontFamily: "'Share Tech Mono',monospace", fontSize: '11px' }}>
                    No {trustTab} transactions yet.
                  </div>
                );
              }
              const counterpartyRole = trustTab === 'seller' ? 'Buyer' : 'Seller';
              return rows.map((row, i) => <TrustHistoryRow key={row.id || i} row={row} viewerSharing={shareHistory} counterpartyRole={counterpartyRole} />);
            })()}
          </div>
        </>
      )}

      <div style={S.trustExplainer}>
        <div style={S.trustExplainerMeta}>// HOW THIS WORKS //</div>
        Trust on CK is built from completed transaction behavior. No stars, no reviews — just what you've actually done.
        <div style={S.trustLegend}>
          <span style={S.trustLegendItem}><span style={{ ...S.trustLegendSwatch, background: 'var(--ok-2)' }} /> <strong style={S.trustLegendStrong}>Green</strong> clean record</span>
          <span style={S.trustLegendItem}><span style={{ ...S.trustLegendSwatch, background: '#eab308' }} /> <strong style={S.trustLegendStrong}>Yellow</strong> ≥5% unresolved</span>
          <span style={S.trustLegendItem}><span style={{ ...S.trustLegendSwatch, background: '#ef4444' }} /> <strong style={S.trustLegendStrong}>Red</strong> ≥5% flagged</span>
          <span style={S.trustLegendItem}><span style={{ ...S.trustLegendSwatchSprout }} /> <strong style={S.trustLegendStrong}>Sprout</strong> new collector</span>
        </div>
        Color reflects last 12 months · Count is lifetime.
      </div>
    </div>
  );
}

function TrustBadgeCell({ stats, role }) {
  const isSprout = !stats;
  const count = stats?.count || 0;
  return (
    <div style={S.trustBadgeCell}>
      <TrustBadge
        color={isSprout ? 'sprout' : stats.color}
        count={count}
        role={role}
        size="lg"
        interactive={false}
      />
      {!isSprout && (
        <div style={S.trustBreakdown}>
          <div style={{ ...S.breakdownChip, background: 'rgba(var(--ok-2-rgb),0.10)' }}>
            <span style={{ ...S.breakdownNum, color: 'var(--ok)' }}>{stats.clean}</span>
            <span style={S.breakdownLbl}>clean</span>
          </div>
          <div style={{ ...S.breakdownChip, background: 'rgba(234,179,8,0.10)' }}>
            <span style={{ ...S.breakdownNum, color: 'var(--warn)' }}>{stats.unresolved}</span>
            <span style={S.breakdownLbl}>unresolved</span>
          </div>
          <div style={{ ...S.breakdownChip, background: 'rgba(var(--err-rgb),0.10)' }}>
            <span style={{ ...S.breakdownNum, color: 'var(--err)' }}>{stats.flagged}</span>
            <span style={S.breakdownLbl}>flagged</span>
          </div>
        </div>
      )}
    </div>
  );
}

function TrustHistoryRow({ row, viewerSharing, counterpartyRole }) {
  const stripe = row.status === 'unresolved' ? 'rgba(234,179,8,0.6)'
               : row.status === 'flagged'    ? 'rgba(var(--err-rgb),0.6)'
               : 'transparent';
  const pillStyle = row.status === 'clean'      ? { bg: 'rgba(var(--ok-2-rgb),0.18)',  fg: 'var(--ok)', icon: '✓' }
                  : row.status === 'unresolved' ? { bg: 'rgba(234,179,8,0.18)',  fg: 'var(--warn)', icon: '⚠' }
                  :                                { bg: 'rgba(var(--err-rgb),0.18)',  fg: 'var(--err)', icon: '✕' };
  return (
    <div style={{ ...S.thxRow, borderLeftColor: stripe }}>
      <div style={S.thxRowTop}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...S.thxPill, background: pillStyle.bg, color: pillStyle.fg }}>
            {pillStyle.icon} {row.status}
          </span>
          <span style={S.thxPrice}>{row.price != null ? `$${row.price}` : '—'}</span>
        </div>
        <span style={S.thxDate}>{row.date}</span>
      </div>
      <div style={S.thxItem}>{row.item}</div>
      <div style={S.thxParty}>
        {row.anon ? (
          // row.anon reflects only the counterparty's privacy flag. When the
          // viewer is sharing their own handle, only the counterparty is hidden
          // — so "both anonymous" would be inaccurate.
          viewerSharing ? (
            <>to CK collector · <span style={S.thxAnon}>{counterpartyRole} stayed anonymous</span></>
          ) : (
            <>to CK collector · <span style={S.thxAnon}>both anonymous</span></>
          )
        ) : (
          <>to <span style={S.thxHandle}>{row.partner}</span></>
        )}
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────
// Phase 2E.1 a11y upgrade: muted text uses var(--muted-5) (~6.8:1) and hint text
// uses #7a8aaa (~5.5:1) — both pass WCAG 2.1 AA on the dark card surface.
// The older var(--muted-2) / #2a3a5a palette is kept for non-text uses only.
const S = {
  page: { minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)', fontFamily: "'Rajdhani',sans-serif" },
  header: { background: 'var(--bg-header)', padding: '20px 24px 0', borderBottom: '1px solid var(--border)', position: 'relative', zIndex: 10 },
  hInner: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', maxWidth: '800px', margin: '0 auto', paddingBottom: '16px' },
  logoLine: { fontFamily: "'Orbitron',monospace", fontSize: '9px', letterSpacing: '4px', color: 'var(--brand-gold)', marginBottom: '6px' },
  title: { fontFamily: "'Orbitron',monospace", fontSize: 'clamp(18px,3vw,28px)', fontWeight: 900, color: 'var(--text-strong)', margin: 0 },
  backBtn: { fontFamily: "'Orbitron',monospace", fontSize: '9px', letterSpacing: '1px', color: 'var(--muted-5)', background: 'transparent', border: '1px solid var(--border)', padding: '7px 12px', borderRadius: '2px', cursor: 'pointer' },
  main: { padding: '24px', maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' },

  // Base card — section variants override border + add a colored left stripe (4px)
  card: { background: 'var(--profile-card)', border: '1px solid var(--border)', borderLeftWidth: '4px', borderRadius: '3px', padding: '20px', position: 'relative' },
  cardTitle: { fontFamily: "'Orbitron',monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--muted-5)', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid var(--border)' },
  cardTitleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid var(--border)' },

  // ─── Section color variants (1px outline @ 0.55, 4px left-edge stripe @ 0.85) ─
  sectionCyan: { borderColor: 'rgba(var(--cyan-rgb),0.55)', borderLeftColor: 'rgba(var(--cyan-rgb),0.85)' },
  sectionGold: { borderColor: 'rgba(var(--gold-rgb),0.55)', borderLeftColor: 'rgba(var(--gold-rgb),0.85)' },
  sectionPurple: { borderColor: 'rgba(var(--purple-rgb),0.55)', borderLeftColor: 'rgba(var(--purple-rgb),0.85)' },
  sectionGreen: { borderColor: 'rgba(var(--ok-2-rgb),0.55)', borderLeftColor: 'rgba(var(--ok-2-rgb),0.85)' },

  // Card-title tints to echo the section outline
  cardTitleCyan: { color: 'var(--card-title-cyan)' },
  cardTitleGold: { color: 'var(--card-title-gold)' },
  cardTitlePurple: { color: 'var(--violet)' },
  cardTitleGreen: { color: 'var(--ok)' },

  // Color key (legend at top of page)
  colorKey: { display: 'flex', flexWrap: 'wrap', gap: '14px', padding: '12px 16px', background: 'rgba(255,255,255,0.02)', border: '0.5px dashed rgba(196,181,253,0.2)', borderRadius: '3px' },
  colorKeyItem: { display: 'flex', alignItems: 'center', gap: '8px', fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--muted-5)' },
  colorKeySwatch: { width: '12px', height: '12px', borderRadius: '2px', border: '0.5px solid rgba(255,255,255,0.2)' },

  // Section header (// TRANSACTION SETUP //)
  sectionHeader: { fontFamily: "'Share Tech Mono',monospace", fontSize: '12px', letterSpacing: '4px', color: 'var(--violet)', padding: '20px 0 4px', marginTop: '8px', borderTop: '0.5px solid rgba(var(--purple-rgb),0.25)' },

  // Avatar editor + bio
  avatarCard: { display: 'flex', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' },
  avatarEditor: { position: 'relative', width: '80px', height: '80px', flexShrink: 0 },
  avatarCircle: { width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(var(--cyan-rgb),0.1)', border: '2px solid var(--cyan)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Orbitron',monospace", fontSize: '28px', fontWeight: 700, color: 'var(--cyan)', overflow: 'hidden', cursor: 'pointer', padding: 0 },
  avatarCircleHasImage: { background: 'rgba(var(--cyan-rgb),0.05)', color: 'transparent' },
  avatarEditOverlay: { position: 'absolute', bottom: 0, right: 0, width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(var(--purple-rgb),0.95)', border: '2px solid var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '13px', color: 'white' },
  avatarInfo: { flex: 1, minWidth: '240px' },
  bigName: { fontFamily: "'Orbitron',monospace", fontSize: '20px', fontWeight: 700, color: 'var(--text-strong)' },
  bigNameRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 },
  verifiedChip: { fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', fontWeight: 500, letterSpacing: '0.04em', padding: '2px 7px', background: 'rgba(var(--ok-2-rgb),0.20)', color: 'var(--ok)', border: '0.5px solid rgba(var(--ok-2-rgb),0.45)', borderRadius: '2px' },
  metaLine: { fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', letterSpacing: '2px', color: 'var(--muted-5)', marginBottom: '10px' },
  publicLink: { fontFamily: "'Orbitron',monospace", fontSize: '9px', letterSpacing: '1px', color: 'var(--cyan)', border: '1px solid rgba(var(--cyan-rgb),0.4)', borderRadius: '2px', padding: '4px 10px', textDecoration: 'none', background: 'rgba(var(--cyan-rgb),0.08)', display: 'inline-block', marginBottom: '14px' },

  bioBlock: { width: '100%', marginTop: '12px' },
  bioLabelRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' },
  bioLabel: { fontFamily: "'Orbitron',monospace", fontSize: '10px', letterSpacing: '2px', color: 'var(--muted-5)' },
  bioCharCount: { fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', color: 'var(--muted-6)' },
  bioCharCountOver: { fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', color: 'var(--err)' },
  bioTextarea: { width: '100%', minHeight: '64px', resize: 'vertical', background: 'var(--panel-deep3)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: "'Rajdhani',sans-serif", fontSize: '14px', lineHeight: 1.5, padding: '10px 14px', borderRadius: '2px', outline: 'none', boxSizing: 'border-box' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '16px' },
  field: { marginBottom: '16px' },
  fieldRow2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  fieldRow3: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px' },
  label: { display: 'block', fontFamily: "'Orbitron',monospace", fontSize: '10px', letterSpacing: '2px', color: 'var(--muted-5)', marginBottom: '6px' },
  labelOptional: { color: 'var(--muted-6)', letterSpacing: 0, fontWeight: 400 },
  input: { width: '100%', background: 'var(--panel-deep3)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: "'Share Tech Mono',monospace", fontSize: '14px', padding: '10px 14px', borderRadius: '2px', outline: 'none', boxSizing: 'border-box' },
  hint: { fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--muted-6)', marginTop: '4px' },
  emailChangeBtn: { fontFamily: "'Orbitron',monospace", fontSize: '9px', letterSpacing: '1.5px', color: 'var(--cyan)', background: 'rgba(var(--cyan-rgb),0.08)', border: '1px solid rgba(var(--cyan-rgb),0.45)', padding: '4px 10px', borderRadius: '2px', cursor: 'pointer' },
  emailChangeBox: { marginTop: '10px', padding: '12px', background: 'var(--panel-deep3)', border: '1px solid var(--border)', borderRadius: '2px' },
  emailPendingLine: { fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', letterSpacing: '2px', color: 'var(--warn)', marginBottom: '6px' },
  emailPendingTarget: { fontFamily: "'Share Tech Mono',monospace", fontSize: '12px', color: 'var(--text)', marginBottom: '8px', wordBreak: 'break-all' },
  emailChangeMsg: { fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--red)', marginTop: '8px', lineHeight: 1.5 },
  emailChangeSubmit: { fontFamily: "'Orbitron',monospace", fontSize: '9px', letterSpacing: '1px', color: 'var(--cyan)', background: 'rgba(var(--cyan-rgb),0.10)', border: '1px solid var(--cyan)', padding: '9px 14px', borderRadius: '2px', cursor: 'pointer' },
  emailChangeCancel: { fontFamily: "'Orbitron',monospace", fontSize: '9px', letterSpacing: '1px', color: 'var(--muted-5)', background: 'transparent', border: '1px solid var(--border)', padding: '9px 14px', borderRadius: '2px', cursor: 'pointer' },
  emailChangeDisabled: { opacity: 0.45, cursor: 'default' },
  hintInfo: { fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--muted-5)', marginTop: '4px', lineHeight: 1.55 },
  hintWarn: { fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--gold)', marginTop: '4px', lineHeight: 1.55 },
  toggle: { width: '44px', height: '26px', borderRadius: '13px', border: '1px solid', cursor: 'pointer', position: 'relative', transition: 'all 0.2s', padding: 0 },
  toggleKnob: { width: '20px', height: '20px', borderRadius: '50%', position: 'absolute', top: '2px', transition: 'all 0.2s' },

  // PRIVATE badge (top-right of PII cards)
  privateBadge: { fontFamily: "'JetBrains Mono',monospace", fontSize: '9px', padding: '2px 6px', background: 'rgba(var(--purple-rgb),0.15)', color: 'var(--violet)', border: '0.5px solid rgba(var(--purple-rgb),0.5)', borderRadius: '2px', letterSpacing: '0.04em' },

  // Payment row layout
  payRow: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '14px', alignItems: 'center', marginBottom: '8px', padding: '12px', background: 'var(--wash-03)', border: '0.5px solid var(--wash-06)', borderRadius: '3px' },
  payLogo: { fontFamily: "'JetBrains Mono',monospace", fontSize: '14px', fontWeight: 500, padding: '6px 12px', borderRadius: '3px' },
  payField: { display: 'flex', gap: '12px', alignItems: 'center' },

  // Stats labels (lighter for AA)
  statLabel: { fontFamily: "'Share Tech Mono',monospace", fontSize: '10px', letterSpacing: '1px', color: 'var(--muted-5)', marginTop: '2px' },

  // Collection export strip (bottom of COLLECTION STATS card)
  exportStrip: { marginTop: '18px', paddingTop: '14px', borderTop: '1px solid var(--border)' },
  exportHint: { fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--muted-6)', lineHeight: 1.5, marginBottom: '10px' },
  exportBtnPrimary: { fontFamily: "'Orbitron',monospace", fontSize: '9px', letterSpacing: '1px', fontWeight: 700, color: 'var(--bg)', background: 'var(--cyan)', border: '1px solid var(--cyan)', borderRadius: '2px', padding: '7px 14px', cursor: 'pointer' },
  exportBtnSecondary: { fontFamily: "'Orbitron',monospace", fontSize: '9px', letterSpacing: '1px', color: 'var(--cyan)', background: 'rgba(var(--cyan-rgb),0.08)', border: '1px solid rgba(var(--cyan-rgb),0.4)', borderRadius: '2px', padding: '7px 14px', cursor: 'pointer' },
  exportBtnDisabled: { color: 'var(--muted-5)', background: 'rgba(138,155,184,0.06)', border: '1px solid var(--border)', cursor: 'default', fontWeight: 400 },
  exportBtnDone: { color: 'var(--ok)', background: 'rgba(var(--ok-rgb),0.08)', border: '1px solid rgba(var(--ok-rgb),0.45)', cursor: 'default', fontWeight: 400 },

  errorMsg: { fontFamily: "'Share Tech Mono',monospace", fontSize: '12px', color: 'var(--err)', border: '1px solid rgba(248,113,113,0.4)', borderRadius: '2px', padding: '10px 14px', background: 'rgba(248,113,113,0.08)' },
  signOutBtn: { fontFamily: "'Orbitron',monospace", fontSize: '10px', letterSpacing: '1px', color: 'var(--err)', background: 'transparent', border: '1px solid rgba(248,113,113,0.5)', padding: '10px 20px', borderRadius: '2px', cursor: 'pointer' },
  saveBtn: { fontFamily: "'Orbitron',monospace", fontSize: '10px', letterSpacing: '1px', color: 'var(--warn)', background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.55)', padding: '10px 20px', borderRadius: '2px', cursor: 'pointer' },

  // ─── Identity verification states ─────────────────────────────────
  verifyIntro: { fontFamily: "'Rajdhani',sans-serif", fontSize: '14px', color: 'var(--text)', lineHeight: 1.5, marginBottom: '12px' },
  getVerifiedBtn: { fontFamily: "'Orbitron',monospace", fontSize: '11px', letterSpacing: '2px', color: 'var(--text-strong)', background: 'rgba(var(--purple-rgb),0.18)', border: '1px solid rgba(var(--purple-rgb),0.6)', padding: '12px 20px', borderRadius: '2px', display: 'inline-block', textDecoration: 'none' },

  verifiedBlock: { padding: '14px 16px', background: 'rgba(var(--ok-2-rgb),0.08)', border: '0.5px solid rgba(var(--ok-2-rgb),0.45)', borderRadius: '4px' },
  verifiedRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 },
  verifiedPill: { fontFamily: "'JetBrains Mono',monospace", fontSize: '11px', fontWeight: 500, letterSpacing: '0.04em', padding: '3px 8px', background: 'rgba(var(--ok-2-rgb),0.22)', color: 'var(--ok)', border: '0.5px solid rgba(var(--ok-2-rgb),0.45)', borderRadius: '2px' },
  verifiedDate: { fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--muted-5)', letterSpacing: '0.02em' },
  verifiedBody: { fontFamily: "'Rajdhani',sans-serif", fontSize: '14px', color: 'var(--text)', lineHeight: 1.5 },

  verifyingBlock: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', background: 'rgba(var(--cyan-rgb),0.08)', border: '0.5px solid rgba(var(--cyan-rgb),0.35)', borderRadius: '4px' },
  verifyingSpinner: { fontFamily: "'JetBrains Mono',monospace", fontSize: '16px', color: 'var(--cyan)', display: 'inline-block', animation: 'pp-spin 1.2s linear infinite' },
  verifyingText: { fontFamily: "'Rajdhani',sans-serif", fontSize: '14px', color: 'var(--text)' },

  lockedBlock: { padding: '14px 16px', background: 'rgba(var(--err-rgb),0.10)', border: '0.5px solid rgba(var(--err-rgb),0.45)', borderRadius: '4px' },
  lockedIcon: { fontSize: '16px', color: 'var(--err)' },
  lockedHeader: { fontFamily: "'Orbitron',monospace", fontSize: '11px', letterSpacing: '1.5px', color: 'var(--err)', textTransform: 'uppercase' },
  lockedBody: { fontFamily: "'Rajdhani',sans-serif", fontSize: '14px', color: 'var(--text)', lineHeight: 1.5, marginBottom: 12 },
  emailKeeperBtn: { fontFamily: "'Orbitron',monospace", fontSize: '10px', letterSpacing: '1.5px', color: 'var(--text-strong)', background: 'rgba(var(--err-rgb),0.20)', border: '1px solid rgba(var(--err-rgb),0.6)', padding: '10px 16px', borderRadius: '2px', textDecoration: 'none', display: 'inline-block' },

  // ─── MARKETPLACE TRUST card (Phase 2E.1 addition) ───────────────────
  // Sprout (new collector) state
  trustSprout: { textAlign: 'center', padding: '22px 16px', background: 'rgba(var(--ok-2-rgb),0.04)', borderRadius: '4px', marginBottom: '14px' },
  trustSproutIcon: { fontSize: '36px', marginBottom: '8px' },
  trustSproutTitle: { fontFamily: "'Rajdhani',sans-serif", fontSize: '14px', color: 'var(--text-bright)', fontWeight: 500, marginBottom: '4px' },
  trustSproutSub: { fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--muted-5)', lineHeight: 1.5 },

  // Badges row (two cells, one per role): side-by-side where they fit,
  // stacked on phone widths — a fixed 1fr 1fr overflows the card there
  // because the 3-chip breakdown can't shrink below ~190px per cell.
  trustBadgesRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '14px' },
  trustBadgeCell: { background: 'var(--wash-03)', border: '0.5px solid var(--wash-08)', borderRadius: '4px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' },

  // 3-col breakdown chips below each badge
  trustBreakdown: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' },
  breakdownChip: { padding: '6px 4px', borderRadius: '3px', textAlign: 'center', fontFamily: "'JetBrains Mono',monospace", display: 'flex', flexDirection: 'column' },
  breakdownNum: { fontSize: '14px', fontWeight: 500, lineHeight: 1.2 },
  breakdownLbl: { fontSize: '9px', color: 'var(--muted-5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '2px' },

  // Tabs row (seller / buyer)
  trustTabs: { display: 'flex', borderBottom: '0.5px solid var(--wash-08)', marginBottom: '12px' },
  trustTab: { flex: 1, padding: '10px 12px', background: 'transparent', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", fontSize: '12px', color: 'var(--muted-5)', textAlign: 'center', minHeight: '44px' },
  trustTabActive: { color: 'var(--ok)', borderBottomColor: 'var(--ok-2)', fontWeight: 500 },

  // Scrollable transaction list container
  trustHistoryScroll: { maxHeight: '360px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '5px', padding: '4px 6px 4px 2px', marginBottom: '14px', scrollbarWidth: 'thin', scrollbarColor: 'rgba(196,181,253,0.3) transparent' },

  // Transaction rows
  thxRow: { background: 'var(--wash-03)', borderRadius: '3px', padding: '9px 10px', borderLeft: '2px solid transparent' },
  thxRowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '3px' },
  thxPill: { fontFamily: "'JetBrains Mono',monospace", fontSize: '9px', fontWeight: 500, padding: '1px 6px', borderRadius: '2px' },
  thxPrice: { fontFamily: "'JetBrains Mono',monospace", fontSize: '12px', fontWeight: 500, color: 'var(--text-bright)' },
  thxDate: { fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', color: 'var(--muted-5)' },
  thxItem: { fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', color: 'var(--muted-5)', marginBottom: '2px' },
  thxParty: { fontFamily: "'JetBrains Mono',monospace", fontSize: '9px', color: 'var(--muted-6)' },
  thxAnon: { color: 'var(--violet)', textDecoration: 'underline', textDecorationStyle: 'dotted' },
  thxHandle: { color: 'var(--violet)' },

  // Explainer block (always visible at bottom of the card)
  trustExplainer: { padding: '12px 14px', background: 'rgba(0,0,0,0.18)', borderRadius: '3px', fontFamily: "'Share Tech Mono',monospace", fontSize: '11px', color: 'var(--muted-5)', lineHeight: 1.65 },
  trustExplainerMeta: { fontSize: '10px', letterSpacing: '0.04em', color: 'var(--muted-6)', textTransform: 'uppercase', marginBottom: '6px' },
  trustLegend: { display: 'flex', flexWrap: 'wrap', gap: '8px 14px', margin: '6px 0' },
  trustLegendItem: { display: 'inline-flex', alignItems: 'center', gap: '5px' },
  trustLegendSwatch: { width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0 },
  trustLegendSwatchSprout: { width: '9px', height: '9px', borderRadius: '50%', background: 'transparent', border: '1px dashed var(--ok)', flexShrink: 0 },
  trustLegendStrong: { color: 'var(--text)', fontWeight: 500 },
};

// Spinner keyframes — appended once to the head when ProfilePage mounts in any browser.
if (typeof document !== 'undefined' && !document.getElementById('pp-spin-kf')) {
  const style = document.createElement('style');
  style.id = 'pp-spin-kf';
  style.textContent = '@keyframes pp-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}
